import { Colonia } from '../../types/colonia';

const paymentApiBaseUrl =
  process.env.REACT_APP_PAYMENT_API_URL ||
  'https://southamerica-east1-coloniadeferiasss.cloudfunctions.net/paymentApi';

const isInlineImage = (value?: string) => Boolean(value?.startsWith('data:image/'));

const uploadRegistrationImages = async (inscricao: Colonia): Promise<Colonia> => {
  const images: Array<{ key: string; dataUrl: string; folder: string }> = [];
  if (isInlineImage(inscricao.fotoUrl)) images.push({ key: 'participant', dataUrl: inscricao.fotoUrl!, folder: 'participants' });
  if (isInlineImage(inscricao.fotoUrlRetirada)) images.push({ key: 'guardian', dataUrl: inscricao.fotoUrlRetirada!, folder: 'guardians' });
  if (isInlineImage(inscricao.assinaturaUrl)) images.push({ key: 'signature', dataUrl: inscricao.assinaturaUrl!, folder: 'signatures' });
  inscricao.pessoasRetirada?.forEach((pessoa, index) => {
    if (isInlineImage(pessoa.fotoUrl)) images.push({ key: `guardian_${index}`, dataUrl: pessoa.fotoUrl!, folder: 'guardians' });
  });

  if (!images.length) return inscricao;
  const response = await fetch(`${paymentApiBaseUrl.replace(/\/$/, '')}/storage/upload-registration-images`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ images })
  });
  const data = await response.json();
  if (!response.ok || !data.success) throw new Error(data.error || 'Não foi possível salvar as imagens no Storage.');
  const urls = data.urls as Record<string, string>;

  return {
    ...inscricao,
    fotoUrl: urls.participant || inscricao.fotoUrl,
    fotoUrlRetirada: urls.guardian || inscricao.fotoUrlRetirada,
    assinaturaUrl: urls.signature || inscricao.assinaturaUrl,
    pessoasRetirada: inscricao.pessoasRetirada?.map((pessoa, index) => ({
      ...pessoa,
      fotoUrl: urls[`guardian_${index}`] || pessoa.fotoUrl
    }))
  };
};

export interface VagasInscricao {
  periodo: string;
  limite: number;
  confirmadas: number;
  restantes: number;
  encerrado: boolean;
}

export const consultarVagas = async (): Promise<VagasInscricao | null> => {
  try {
    const response = await fetch(`${paymentApiBaseUrl.replace(/\/$/, '')}/vagas`);
    const payload = await response.json();
    if (!response.ok || !payload.success) return null;
    return payload as VagasInscricao;
  } catch {
    // Sem resposta do servidor o formulário continua aberto; o POST ainda barra se estiver cheio.
    return null;
  }
};

export const criarInscricao = async (inscricao: Colonia) => {
  const inscricaoComImagens = await uploadRegistrationImages(inscricao);
  const response = await fetch(`${paymentApiBaseUrl.replace(/\/$/, '')}/admin/inscricoes`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(inscricaoComImagens)
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.success) {
    throw new Error(payload?.error || 'Não foi possível salvar a inscrição.');
  }
  return payload.inscricao as Colonia;
};

export interface CheckoutPaymentPayload {
  baseValue: number;
  billingType: 'PIX' | 'CREDIT_CARD';
  installments: number;
  registrationReference: string;
  description: string;
  payer: {
    name: string;
    cpf: string;
    email: string;
    phone: string;
    postalCode: string;
    address: string;
    addressNumber: string;
    addressComplement?: string;
    province: string;
  };
  card?: {
    holderName: string;
    number: string;
    expiryMonth: string;
    expiryYear: string;
    ccv: string;
  };
}

export interface CheckoutPaymentResult {
  id: string;
  status: string;
  billingType: 'PIX' | 'CREDIT_CARD';
  value: number;
  invoiceUrl?: string;
  pixQrCode?: string;
  pixQrCodeUrl?: string;
  pixExpirationDate?: string;
  installmentCount?: number;
  installmentValue?: number;
}

export const criarPagamento = async (payload: CheckoutPaymentPayload) => {
  const response = await fetch(`${paymentApiBaseUrl.replace(/\/$/, '')}/create`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok || !data.success || !data.payment) {
    throw new Error(data.error || 'Não foi possível criar o pagamento.');
  }
  return data.payment as CheckoutPaymentResult;
};

export const consultarPagamento = async (paymentId: string) => {
  const response = await fetch(`${paymentApiBaseUrl.replace(/\/$/, '')}/status?paymentId=${encodeURIComponent(paymentId)}`);
  const data = await response.json();
  if (!response.ok || !data.success || !data.payment) {
    throw new Error(data.error || 'Não foi possível consultar o pagamento.');
  }
  return data.payment as CheckoutPaymentResult;
};

export const confirmarPagamentoInscricao = async (registrationId: string, payment: CheckoutPaymentResult) => {
  const response = await fetch(`${paymentApiBaseUrl.replace(/\/$/, '')}/confirm-registration`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ registrationId, payment })
  });
  const data = await response.json();
  if (!response.ok || !data.success) throw new Error(data.error || 'Não foi possível atualizar a inscrição.');
};

export interface PremioGanho {
  sorteioId: string;
  titulo: string;
  premio: string;
  sorteadoEm: string | null;
}

export interface ConfirmedRegistration {
  id: string;
  nomeCompleto: string;
  telefoneMae: string;
  valorTotal: number;
  gratuito: boolean;
  status: 'confirmado';
  premios?: PremioGanho[];
}

export const consultarInscricaoConfirmada = async (registrationId: string) => {
  const response = await fetch(
    `${paymentApiBaseUrl.replace(/\/$/, '')}/registration/${encodeURIComponent(registrationId)}`
  );
  const data = await response.json();
  if (!response.ok || !data.success || !data.registration) {
    throw new Error(data.error || 'Não foi possível carregar a confirmação.');
  }
  return data.registration as ConfirmedRegistration;
};

export const notificarNovaInscricao = async (registrationId: string) => {
  const response = await fetch(`${paymentApiBaseUrl.replace(/\/$/, '')}/messaging/notify-registration`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ registrationId })
  });
  const data = await response.json();
  if (!response.ok || !data.success) throw new Error(data.error || 'Não foi possível enviar as mensagens.');
  return data.result;
};
